import React, { useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { ClinicalGuides } from '../App';

// --- TYPE DEFINITIONS ---
type Point = { x: number; y: number };
type Tool = 'midline' | 'interpupillary' | 'smileArc' | 'pan';
type GuideKeys = 'midline' | 'interpupillary' | 'smileArc';

// --- PROPS INTERFACE ---
interface ClinicalGuideEditorProps {
  imageUrl: string;
  onConfirm: (guides: ClinicalGuides) => void;
  onCancel: () => void;
  initialGuides: ClinicalGuides | null;
}

// --- INLINE ICON DEFINITIONS ---
const XIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const CheckCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></svg>
);
const ZoomInIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" /></svg>
);
const ZoomOutIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" /></svg>
);
const HandIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" /><path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" /><path d="M10 9V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v5" /><path d="M6 14v-1a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h1.5" /><path d="M18 18.5a1.5 1.5 0 0 1-3 0V12a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v1.5" /><path d="M22 14h-2.5a1.5 1.5 0 0 0-1.5 1.5V22" /></svg>
);
const FitToScreenIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M15 3h6v6" /><path d="M9 3H3v6" /><path d="M15 21h6v-6" /><path d="M9 21H3v-6" /></svg>
);
const MidlineIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><line x1="12" y1="3" x2="12" y2="21" /><line x1="3" y1="12" x2="5" y2="12" /><line x1="19" y1="12" x2="21" y2="12" /></svg>
);
const InterpupillaryIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="6.5" cy="12.5" r="1.5" /><circle cx="17.5" cy="12.5" r="1.5" /><path d="M2 12.5h20" /></svg>
);
const SmileArcIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M2 12c1-3 4-6 10-6s9 3 10 6" /></svg>
);
const Trash2Icon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
);

// --- HELPER FUNCTION for Circular Arc ---
const drawCircularArc = (
  ctx: CanvasRenderingContext2D,
  p1: Point, p2: Point, p3: Point,
  color: string, lineWidth: number,
  options: { alpha?: number, lineDash?: number[] } = {}
) => {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.globalAlpha = options.alpha || 1;
  if (options.lineDash) ctx.setLineDash(options.lineDash);

  // Check for collinearity to avoid division by zero
  const crossProduct = (p2.y - p1.y) * (p3.x - p2.x) - (p2.x - p1.x) * (p3.y - p2.y);
  if (Math.abs(crossProduct) < 1e-6) {
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p3.x, p3.y);
    ctx.stroke();
    ctx.restore();
    return;
  }

  const D = 2 * (p1.x * (p2.y - p3.y) + p2.x * (p3.y - p1.y) + p3.x * (p1.y - p2.y));

  ctx.beginPath();
  const p1Sq = p1.x * p1.x + p1.y * p1.y;
  const p2Sq = p2.x * p2.x + p2.y * p2.y;
  const p3Sq = p3.x * p3.x + p3.y * p3.y;

  const centerX = (p1Sq * (p2.y - p3.y) + p2Sq * (p3.y - p1.y) + p3Sq * (p1.y - p2.y)) / D;
  const centerY = (p1Sq * (p3.x - p2.x) + p2Sq * (p1.x - p3.x) + p3Sq * (p2.x - p1.x)) / D;
  const radius = Math.hypot(p1.x - centerX, p1.y - centerY);

  const startAngle = Math.atan2(p1.y - centerY, p1.x - centerX);
  const midAngle = Math.atan2(p2.y - centerY, p2.x - centerX);
  const endAngle = Math.atan2(p3.y - centerY, p3.x - centerX);

  const normalize = (angle: number) => (angle + 2 * Math.PI) % (2 * Math.PI);
  const sNorm = normalize(startAngle);
  const mNorm = normalize(midAngle);
  const eNorm = normalize(endAngle);

  let anticlockwise: boolean;
  if (sNorm < eNorm) {
    anticlockwise = mNorm > sNorm && mNorm < eNorm;
  } else {
    anticlockwise = mNorm > sNorm || mNorm < eNorm;
  }

  ctx.arc(centerX, centerY, radius, startAngle, endAngle, !anticlockwise);
  ctx.stroke();
  ctx.restore();
};

// --- MAIN COMPONENT ---
const ClinicalGuideEditor: React.FC<ClinicalGuideEditorProps> = ({ imageUrl, onConfirm, onCancel, initialGuides }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(new Image());
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  const [guides, setGuides] = useState<ClinicalGuides>({ midline: null, interpupillary: null, smileArc: null });
  const [activeTool, setActiveTool] = useState<Tool>('midline');
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [viewState, setViewState] = useState({ scale: 1, x: 0, y: 0 });
  const [smileArcPoints, setSmileArcPoints] = useState<Point[]>([]);
  const [tempPoint, setTempPoint] = useState<Point | null>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image.src || image.naturalWidth === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(viewState.x, viewState.y);
    ctx.scale(viewState.scale, viewState.scale);
    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);

    const baseLineWidth = 3 / viewState.scale;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
    ctx.shadowBlur = 8 / viewState.scale;
    ctx.lineCap = 'round';

    // Draw Midline
    if (guides.midline) {
      ctx.beginPath();
      ctx.moveTo(guides.midline.start.x, guides.midline.start.y);
      ctx.lineTo(guides.midline.end.x, guides.midline.end.y);
      ctx.strokeStyle = '#FFD700';
      ctx.lineWidth = baseLineWidth;
      ctx.setLineDash([]);
      ctx.stroke();
    }
    // Draw Interpupillary
    if (guides.interpupillary) {
      ctx.beginPath();
      ctx.moveTo(guides.interpupillary.start.x, guides.interpupillary.start.y);
      ctx.lineTo(guides.interpupillary.end.x, guides.interpupillary.end.y);
      ctx.strokeStyle = '#00FFFF';
      ctx.lineWidth = baseLineWidth;
      ctx.setLineDash([baseLineWidth * 3, baseLineWidth * 2]);
      ctx.stroke();
    }
    // Draw Confirmed Smile Arc
    if (guides.smileArc) {
      drawCircularArc(ctx, guides.smileArc.p1, guides.smileArc.p2, guides.smileArc.p3, '#FF00FF', baseLineWidth);
    }
    // Draw Smile Arc Creation Process (Previews)
    if (activeTool === 'smileArc') {
      // Draw placed points
      smileArcPoints.forEach((p) => {
        ctx.save();
        ctx.fillStyle = '#FF00FF'; ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 1.5 / viewState.scale; ctx.shadowColor = 'transparent';
        ctx.beginPath(); ctx.arc(p.x, p.y, 6 / viewState.scale, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.restore();
      });

      // Draw previews based on tempPoint (mouse position)
      if (tempPoint) {
        if (smileArcPoints.length === 1) { // Line preview from first point to mouse
          ctx.save();
          ctx.beginPath(); ctx.moveTo(smileArcPoints[0].x, smileArcPoints[0].y); ctx.lineTo(tempPoint.x, tempPoint.y);
          ctx.strokeStyle = '#FF00FF'; ctx.lineWidth = baseLineWidth; ctx.setLineDash([baseLineWidth * 2, baseLineWidth * 2]); ctx.globalAlpha = 0.7;
          ctx.stroke();
          ctx.restore();
        } else if (smileArcPoints.length === 2) { // Arc preview using endpoints and mouse as peak
          drawCircularArc(ctx, smileArcPoints[0], tempPoint, smileArcPoints[1], '#FF00FF', baseLineWidth, { alpha: 0.7, lineDash: [baseLineWidth * 2, baseLineWidth * 2] });
        }
      }
    }

    ctx.restore();
  }, [guides, viewState, activeTool, smileArcPoints, tempPoint]);

  const resetView = useCallback(() => {
    const container = containerRef.current; const canvas = canvasRef.current; const image = imageRef.current;
    if (container && canvas && image.naturalWidth > 0) {
      const { width: cW, height: cH } = container.getBoundingClientRect();
      canvas.width = cW; canvas.height = cH;
      const { naturalWidth: iW, naturalHeight: iH } = image;
      const scale = Math.min(cW / iW, cH / iH) * 0.95;
      setViewState({ scale, x: (cW - iW * scale) / 2, y: (cH - iH * scale) / 2 });
    }
  }, []);

  useEffect(() => {
    const image = imageRef.current; image.crossOrigin = "Anonymous"; image.src = imageUrl;
    image.onload = () => {
      resetView();
      if (initialGuides && image.naturalWidth > 0) {
        const unnormalize = (p: Point) => ({ x: p.x * image.naturalWidth, y: p.y * image.naturalHeight });
        setGuides({
          midline: initialGuides.midline ? { start: unnormalize(initialGuides.midline.start), end: unnormalize(initialGuides.midline.end) } : null,
          interpupillary: initialGuides.interpupillary ? { start: unnormalize(initialGuides.interpupillary.start), end: unnormalize(initialGuides.interpupillary.end) } : null,
          smileArc: initialGuides.smileArc ? { p1: unnormalize(initialGuides.smileArc.p1), p2: unnormalize(initialGuides.smileArc.p2), p3: unnormalize(initialGuides.smileArc.p3) } : null,
        });
      }
    };
    window.addEventListener('resize', resetView);
    return () => window.removeEventListener('resize', resetView);
  }, [imageUrl, resetView, initialGuides]);

  useEffect(() => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(draw);
  }, [draw]);

  const getWorldMousePos = (e: React.MouseEvent): Point => {
    const canvas = canvasRef.current; if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: (e.clientX - rect.left - viewState.x) / viewState.scale, y: (e.clientY - rect.top - viewState.y) / viewState.scale };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const worldPos = getWorldMousePos(e);
    if (activeTool === 'pan') {
      setIsDrawing(true); setStartPoint({ x: e.clientX - viewState.x, y: e.clientY - viewState.y });
    } else if (activeTool === 'midline' || activeTool === 'interpupillary') {
      setIsDrawing(true); setStartPoint(worldPos);
    } else if (activeTool === 'smileArc') {
      const newPoints = [...smileArcPoints, worldPos];
      setSmileArcPoints(newPoints);
      if (newPoints.length === 3) {
        setGuides(g => ({ ...g, smileArc: { p1: newPoints[0], p2: newPoints[2], p3: newPoints[1] } }));
        setSmileArcPoints([]);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const worldPos = getWorldMousePos(e);
    if (isDrawing) {
      if (activeTool === 'pan' && startPoint) {
        setViewState(v => ({ ...v, x: e.clientX - startPoint.x, y: e.clientY - startPoint.y }));
      } else if (startPoint && (activeTool === 'midline' || activeTool === 'interpupillary')) {
        setGuides(g => ({ ...g, [activeTool]: { start: startPoint, end: worldPos } }));
      }
    } else { // Handle previews when not drawing/dragging
      if (activeTool === 'smileArc' && smileArcPoints.length > 0 && smileArcPoints.length < 3) {
        setTempPoint(worldPos);
      } else if (tempPoint) {
        setTempPoint(null);
      }
    }
  };

  const handleZoom = useCallback((factor: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const newScale = Math.max(0.1, Math.min(viewState.scale * factor, 20));
    const worldX = (centerX - viewState.x) / viewState.scale;
    const worldY = (centerY - viewState.y) / viewState.scale;
    const newX = centerX - worldX * newScale;
    const newY = centerY - worldY * newScale;
    setViewState({ scale: newScale, x: newX, y: newY });
  }, [viewState]);

  const handleMouseUp = () => { setIsDrawing(false); setStartPoint(null); };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault(); const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect(); const mouseX = e.clientX - rect.left; const mouseY = e.clientY - rect.top;
    const scaleAmount = 1.1; const delta = e.deltaY > 0 ? 1 / scaleAmount : scaleAmount;
    const newScale = Math.max(0.1, Math.min(viewState.scale * delta, 20));
    const worldX = (mouseX - viewState.x) / viewState.scale; const worldY = (mouseY - viewState.y) / viewState.scale;
    const newX = mouseX - worldX * newScale; const newY = mouseY - worldY * newScale;
    setViewState({ scale: newScale, x: newX, y: newY });
  };

  const handleClearGuide = (guide: GuideKeys) => {
    if (guide === 'smileArc') setSmileArcPoints([]);
    setGuides(g => ({ ...g, [guide]: null }));
  };

  const handleConfirm = () => {
    const image = imageRef.current;
    if (image.naturalWidth === 0) { onCancel(); return; }
    const normalize = (p: Point) => ({ x: p.x / image.naturalWidth, y: p.y / image.naturalHeight });
    const normalizedGuides: ClinicalGuides = {
      midline: guides.midline ? { start: normalize(guides.midline.start), end: normalize(guides.midline.end) } : null,
      interpupillary: guides.interpupillary ? { start: normalize(guides.interpupillary.start), end: normalize(guides.interpupillary.end) } : null,
      smileArc: guides.smileArc ? { p1: normalize(guides.smileArc.p1), p2: normalize(guides.smileArc.p2), p3: normalize(guides.smileArc.p3) } : null,
    };
    onConfirm(normalizedGuides);
  };

  const toolConfig = {
    midline: { cursor: 'crosshair', icon: <MidlineIcon className="w-6 h-6" />, name: 'Facial Midline', color: 'text-yellow-400', instruction: 'Click and drag vertically to set the facial midline.' },
    interpupillary: { cursor: 'crosshair', icon: <InterpupillaryIcon className="w-6 h-6" />, name: 'Interpupillary Line', color: 'text-cyan-400', instruction: 'Click and drag horizontally to set the interpupillary line.' },
    smileArc: {
      cursor: 'crosshair', icon: <SmileArcIcon className="w-6 h-6" />, name: 'Smile Arc', color: 'text-fuchsia-500',
      instruction: (points: Point[]) => {
        switch (points.length) {
          case 0: return 'Click to set the first endpoint of the smile arc.';
          case 1: return 'Click to set the second endpoint of the smile arc.';
          case 2: return 'Click to set the peak of the curve.';
          default: return 'Click to set the first endpoint of the smile arc.';
        }
      }
    },
    pan: { cursor: 'grab', icon: <HandIcon className="w-6 h-6" />, name: 'Pan & Zoom', color: 'text-zinc-300', instruction: 'Click and drag to pan. Use mouse wheel to zoom.' },
  };

  const getInstruction = () => {
    const tool = toolConfig[activeTool];
    return typeof tool.instruction === 'function' ? tool.instruction(smileArcPoints) : tool.instruction;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-zinc-950 text-white"
    >
      <header className="absolute top-0 left-0 right-0 z-30 p-4 flex justify-between items-center bg-gradient-to-b from-black/60 to-transparent">
        <h2 className="text-xl font-bold text-zinc-100">Clinical Guide Editor</h2>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-semibold text-zinc-300 bg-zinc-800/80 rounded-lg hover:bg-zinc-700 transition-colors">Cancel</button>
          <button onClick={handleConfirm} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors">
            <CheckCircleIcon className="w-5 h-5" /> Confirm Guides
          </button>
        </div>
      </header>

      <main ref={containerRef} className="w-full h-full" onWheel={handleWheel}>
        <canvas ref={canvasRef} className="w-full h-full"
          style={{ cursor: isDrawing && activeTool === 'pan' ? 'grabbing' : toolConfig[activeTool].cursor }}
          onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} />
      </main>

      {/* Instruction Bar */}
      <motion.div
        key={getInstruction()} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="absolute top-20 left-1/2 -translate-x-1/2 z-20 px-4 py-2 text-sm font-medium rounded-lg bg-black/50 backdrop-blur-md border border-white/10"
      >
        <p>{getInstruction()}</p>
      </motion.div>

      {/* Toolbar */}
      <motion.div
        initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2, duration: 0.5 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 p-2 rounded-full bg-zinc-950/80 backdrop-blur-xl border border-white/10 shadow-2xl"
      >
        {(Object.keys(toolConfig) as Tool[]).map(tool => (
          <button key={tool} onClick={() => { setActiveTool(tool); if (tool !== 'smileArc') setSmileArcPoints([]); }} title={toolConfig[tool].name}
            className={`relative w-12 h-12 rounded-full flex items-center justify-center transition-colors ${activeTool === tool ? toolConfig[tool].color : 'text-zinc-300 hover:bg-white/10'}`}>
            {activeTool === tool && <motion.div layoutId="guide-tool-active" className="absolute inset-0 bg-white/10 rounded-full" />}
            <span className="relative z-10">{toolConfig[tool].icon}</span>
          </button>
        ))}
        <div className="w-px h-8 bg-white/10 mx-2" />
        <button onClick={resetView} title="Reset View" className="w-12 h-12 rounded-full flex items-center justify-center text-zinc-300 hover:bg-white/10"><FitToScreenIcon className="w-6 h-6" /></button>
        <button onClick={() => { handleZoom(1.2) }} title="Zoom In" className="w-12 h-12 rounded-full flex items-center justify-center text-zinc-300 hover:bg-white/10"><ZoomInIcon className="w-6 h-6" /></button>
        <button onClick={() => { handleZoom(0.8) }} title="Zoom Out" className="w-12 h-12 rounded-full flex items-center justify-center text-zinc-300 hover:bg-white/10"><ZoomOutIcon className="w-6 h-6" /></button>
      </motion.div>

      {/* Side Panel */}
      <motion.aside
        initial={{ x: '100%' }} animate={{ x: '0' }} transition={{ delay: 0.3, duration: 0.5, ease: 'easeOut' }}
        className="absolute right-0 top-0 bottom-0 z-10 w-64 p-4 pt-20 bg-zinc-950/70 backdrop-blur-xl border-l border-white/10"
      >
        <h3 className="font-bold text-zinc-100 mb-4 text-center">Defined Guides</h3>
        <div className="space-y-3">
          {(Object.keys(guides) as GuideKeys[]).map(key => (
            <div key={key} className="p-2.5 rounded-lg bg-white/5 border border-white/10 flex justify-between items-center">
              <div className={`flex items-center gap-3 ${guides[key] || (key === 'smileArc' && smileArcPoints.length > 0) ? toolConfig[key].color : 'text-zinc-500'}`}>
                {React.isValidElement(toolConfig[key].icon) ? React.cloneElement(toolConfig[key].icon as React.ReactElement<any>, { className: 'w-5 h-5' }) : null}
                <span className="font-semibold text-sm">{toolConfig[key].name}</span>
              </div>
              {(guides[key] || (key === 'smileArc' && smileArcPoints.length > 0)) && (
                <button onClick={() => handleClearGuide(key)} className="p-1 rounded-full text-red-500 hover:bg-red-500/20">
                  <Trash2Icon className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </motion.aside>
    </motion.div>
  );
};
export default ClinicalGuideEditor;
